import React, { useContext, useEffect, useState } from "react";
import { Button, theme } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { Error, Success } from "../../../commons/Messages/Messages";
import { isAuthenticated } from "../../../commons/Auth/auth";
import GeneralTable from "../../../commons/GeneralTable";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { SocketContext } from "../../../context/SocketContext";
import PageToolbar from "../../../design-system/PageToolbar";
import "./Appointments.css";
import { sendAppointment, useAppointmentsLive } from "../../../commons/Appointments/appointmentApi";

import { useTranslation } from "react-i18next";
const ClientAppointments = () => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const socket = useContext(SocketContext);
  const user = isAuthenticated();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [pageSize] = useState(15);

  const getData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/appointments`,
        {
          pageSize,
          pageNo: pageNo - 1,
          searchText,
          client: isAuthenticated()?.serviceProviderId,
          multipleAppointments: true
        },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      );
      if (res.status === 200 || res.status === 304) {
        setData(res.data.data);
        setTotalCount(res.data.count);
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      console.error(err);
      Error(err.response?.data?.errorMessage || err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmSchedule = async (appointmentId, scheduleIndex) => {
    try {
      // The server notifies the provider's calendar itself (useAppointmentsLive there).
      const res = await sendAppointment(
        "put",
        "/confirm-schedule",
        { appointmentId, scheduleIndex },
        t
      );
      if (res && (res.status === 200 || res.status === 304)) {
        Success(res.data.successMessage);
        getData();
      } else if (res) {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      console.error(err);
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  useEffect(() => {
    getData();
  }, [pageNo, searchText]);

  // Refetch when the provider adds, changes or deletes one of this client's appointments.
  useAppointmentsLive(socket, () => getData());

  const columns = [
    {
      title: "#",
      dataIndex: "_id",
      mobile: "hidden",
      render: (text, record, index) => index + 1
    },
    {
      title: t("Appointment Title"),
      dataIndex: "title",
      mobile: "title",
      sorter: (a, b) => a.title?.localeCompare(b.title)
    },
    {
      title: t("Timings"),
      dataIndex: "appointmentSchedules",
      render: (schedules, record) => {
        if (Array.isArray(schedules) && schedules.length > 0) {
          return (
            <div className="client-appointments__slots">
              {schedules.map((schedule, index) => (
                <div key={index} className="client-appointments__slot">
                  <span className="client-appointments__time">
                    {moment(schedule.date).format("MM/DD/YYYY")}{" "}
                    {moment(schedule.startTime).format("HH:mm")}–
                    {moment(schedule.endTime).format("HH:mm")}
                  </span>
                  <Button
                    onClick={() => confirmSchedule(record._id, index)}
                    icon={<CheckCircleOutlined style={{ color: token.colorSuccess }} />}
                    iconPosition="end"
                  >
                    {t("Confirm")}
                  </Button>
                </div>
              ))}
            </div>
          );
        } else {
          return (
            <span className="client-appointments__time">
              {record.start ? moment(record.start).format("MM/DD/YYYY HH:mm") : "--"} –{" "}
              {record.end ? moment(record.end).format("MM/DD/YYYY HH:mm") : "--"}
            </span>
          );
        }
      }
    },
    {
      title: t("Employees"),
      dataIndex: "employees",
      render: employees =>
        employees && employees.length > 0
          ? employees.map(emp => `${emp.firstName} ${emp.lastName}`).join(", ")
          : "--"
    },
    {
      title: t("Description"),
      dataIndex: "desc"
    }
  ];

  const handleSearch = value => {
    setSearchText(value);
    setPageNo(1);
  };

  return (
    <div className="client-appointments">
      <PageToolbar filters={<SearchBarComp handleSearch={handleSearch} />} />
      <GeneralTable
        loading={loading}
        pageNo={pageNo}
        pageSize={pageSize}
        totalCount={totalCount}
        data={data}
        columns={columns}
        setPageNo={setPageNo}
      />
    </div>
  );
};

export default ClientAppointments;
